import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BusinessPositionResponse,
  CogsReplanStateResponse,
  FinanceSummaryResponse,
  FinanceSummarySourcesResponse,
  type BusinessPositionGrade,
  type CogsReplanState,
  type FinanceSummaryCoverage,
  type FinanceSummaryIncomeStatus,
  type FinanceSummaryPayout,
  type FinanceSummarySettlementActivity,
  type FinanceSummarySource,
  type FinanceSummaryTask10Coverage,
  type FeeCenterActivityCategory,
} from '@sourcito/schemas';
import { apiFetch } from './api';
import {
  feeCenterActivityDirection,
  formatFeeCenterCurrency,
  formatFeeCenterDate,
  formatFeeCenterDelta,
} from './fee-center';

export interface FinancePeriod {
  /** Complete UTC calendar-month bounds in YYYY-MM-DD format. */
  from: string;
  to: string;
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** The default view: the three most recently completed UTC calendar months. */
export function defaultFinancePeriod(now: Date = new Date()): FinancePeriod {
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 3, 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  return { from: isoDate(from), to: isoDate(to) };
}

/** Convert inclusive YYYY-MM month controls into complete UTC month bounds. */
export function normalizeFinancePeriod(fromMonth: string, toMonth: string): FinancePeriod | null {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(fromMonth) || !/^\d{4}-(0[1-9]|1[0-2])$/.test(toMonth)) {
    return null;
  }
  const [fromYear, fromNumber] = fromMonth.split('-').map(Number);
  const [toYear, toNumber] = toMonth.split('-').map(Number);
  const from = new Date(Date.UTC(fromYear!, fromNumber! - 1, 1));
  const to = new Date(Date.UTC(toYear!, toNumber!, 0));
  if (from > to || to.getTime() - from.getTime() + 1 > 366 * 24 * 60 * 60 * 1000) return null;
  return { from: isoDate(from), to: isoDate(to) };
}

export type FinancePeriodField = 'from' | 'to';

export interface FinancePeriodTransition {
  period: FinancePeriod;
  error: string | null;
}

/** Apply a month control edit without ever replacing a valid period silently. */
export function transitionFinancePeriod(
  period: FinancePeriod,
  field: FinancePeriodField,
  value: string
): FinancePeriodTransition {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) {
    return { period, error: 'Enter a valid calendar month.' };
  }
  const fromMonth = field === 'from' ? value : period.from.slice(0, 7);
  const toMonth = field === 'to' ? value : period.to.slice(0, 7);
  if (fromMonth > toMonth) {
    return {
      period,
      error:
        field === 'from'
          ? 'Start month must be on or before end month.'
          : 'End month must be on or after start month.',
    };
  }
  const next = normalizeFinancePeriod(fromMonth, toMonth);
  return next
    ? { period: next, error: null }
    : { period, error: 'Select a period of 366 days or less.' };
}

/** 'YYYY-MM' keys for every calendar month in a FinancePeriod, in
 * chronological order — the income statement's column set. */
export function financePeriodMonthKeys(period: FinancePeriod): string[] {
  const [fromYear, fromMonth] = period.from.slice(0, 7).split('-').map(Number);
  const [toYear, toMonth] = period.to.slice(0, 7).split('-').map(Number);
  if (!fromYear || !fromMonth || !toYear || !toMonth) return [];
  const months: string[] = [];
  let year = fromYear;
  let month = fromMonth;
  while (year < toYear || (year === toYear && month <= toMonth)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

/** 'YYYY-MM' -> 'June' (month name only, no year). */
export function financeMonthName(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return new Date(Date.UTC(year, monthNumber - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    timeZone: 'UTC',
  });
}

/** "August 2026" for a single month; "June–July 2026" for a range within one
 * year; "December 2026–January 2027" across a year boundary — the header's
 * period line and the "all figures in" status sentence. */
export function financePeriodRangeLabel(monthKeys: readonly string[]): string {
  if (monthKeys.length === 0) return '';
  const first = monthKeys[0]!;
  const last = monthKeys[monthKeys.length - 1]!;
  if (first === last) return expenseAttestationMonthLabel(first);
  const firstYear = first.slice(0, 4);
  const lastYear = last.slice(0, 4);
  if (firstYear === lastYear) {
    return `${financeMonthName(first)}–${financeMonthName(last)} ${lastYear}`;
  }
  return `${expenseAttestationMonthLabel(first)}–${expenseAttestationMonthLabel(last)}`;
}

const SMALL_NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
] as const;

/** Numbers under ten read as words ("two things"); ten and up stay digits. */
function financeSpellCount(count: number): string {
  return count < SMALL_NUMBER_WORDS.length ? SMALL_NUMBER_WORDS[count]! : String(count);
}

function financeCapitalize(word: string): string {
  return word.length === 0 ? word : `${word[0]!.toUpperCase()}${word.slice(1)}`;
}

/** The Finance header's single status sentence: everything's in, or a count
 * of things holding up the numbers — attributed to one month when every
 * Needs-you item shares it, otherwise the whole period. */
export function financeStatusSentence(
  periodLabel: string,
  needsYouItems: readonly { month?: string }[]
): string {
  const count = needsYouItems.length;
  if (count === 0) return `All figures in for ${periodLabel}.`;
  const word = financeCapitalize(financeSpellCount(count));
  const verb = count === 1 ? 'is' : 'are';
  const months = new Set(needsYouItems.map((item) => item.month));
  const onlyMonth = months.size === 1 ? [...months][0] : undefined;
  const label = onlyMonth ? expenseAttestationMonthLabel(onlyMonth) : periodLabel;
  return `${word} thing${count === 1 ? '' : 's'} ${verb} holding up ${label}.`;
}

function financeJoinWithAnd(items: readonly string[]): string {
  if (items.length === 1) return items[0]!;
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/** "June and July expenses confirmed Sep 2, 2026." — confirmed months
 * grouped by their (already-formatted) confirmation date, in month order;
 * multiple dates join as separate sentences. Null when nothing is
 * confirmed. */
export function financeAttestedMonthsFootnote(
  monthKeys: readonly string[],
  attestedMonths: readonly { month: string; attestedAtLabel: string }[]
): string | null {
  const byDate = new Map<string, string[]>();
  const dateOrder: string[] = [];
  for (const month of monthKeys) {
    const record = attestedMonths.find((entry) => entry.month === month);
    if (!record) continue;
    if (!byDate.has(record.attestedAtLabel)) {
      byDate.set(record.attestedAtLabel, []);
      dateOrder.push(record.attestedAtLabel);
    }
    byDate.get(record.attestedAtLabel)!.push(financeMonthName(month));
  }
  if (dateOrder.length === 0) return null;
  return dateOrder
    .map((date) => `${financeJoinWithAnd(byDate.get(date)!)} expenses confirmed ${date}.`)
    .join(' ');
}

/** Guard API requests against partial-month values from a stale UI state. */
export function isCompleteFinancePeriod(period: FinancePeriod): boolean {
  const from = new Date(`${period.from}T00:00:00.000Z`);
  const to = new Date(`${period.to}T23:59:59.999Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from > to) return false;
  const expected = normalizeFinancePeriod(period.from.slice(0, 7), period.to.slice(0, 7));
  return expected?.from === period.from && expected.to === period.to;
}

export function useFinanceSummary(period: FinancePeriod, enabled = true) {
  return useQuery({
    queryKey: ['finance-summary', period.from, period.to] as const,
    enabled: enabled && isCompleteFinancePeriod(period),
    queryFn: () =>
      apiFetch(
        `/api/reports/finance-summary?from=${encodeURIComponent(period.from)}&to=${encodeURIComponent(period.to)}`,
        { responseSchema: FinanceSummaryResponse }
      ),
  });
}

/** R2 item 2 (A1): the COGS "source evidence" drill-down, fetched lazily —
 * `enabled` should be false until the "How these numbers are built"
 * disclosure is actually opened, so a visit that never opens it never pays
 * for this request. */
export function useFinanceSummarySources(period: FinancePeriod, enabled = true) {
  return useQuery({
    queryKey: ['finance-summary-sources', period.from, period.to] as const,
    enabled: enabled && isCompleteFinancePeriod(period),
    queryFn: () =>
      apiFetch(
        `/api/reports/finance-summary/sources?from=${encodeURIComponent(period.from)}&to=${encodeURIComponent(period.to)}`,
        { responseSchema: FinanceSummarySourcesResponse }
      ),
  });
}

// ─────────────────────────────────────────────────────────
// R2 item 4 (docs/plans/2026-09-03-business-position.md): "every cent I
// invested" panel, fetched lazily — `enabled` should stay false until the
// "Business position" disclosure is opened.
// ─────────────────────────────────────────────────────────

export function useBusinessPosition(enabled: boolean) {
  return useQuery({
    queryKey: ['business-position'] as const,
    enabled,
    queryFn: () =>
      apiFetch('/api/reports/business-position', { responseSchema: BusinessPositionResponse }),
  });
}

/** Muted badge text shown next to a line whose value fell back to an
 *  estimate — never shown for 'settlement'-grade lines (the copy
 *  convention item 3 introduces: only call out the exception). */
export function businessPositionGradeLabel(grade: BusinessPositionGrade): string | null {
  return grade === 'estimate' ? 'estimate' : null;
}

/** "Since Jan 5, 2026 · as of Jan 21, 2026" — plain dates, no ISO strings
 *  in the visible UI (same convention as formatFinanceDate). */
export function businessPositionSinceAsOfLabel(since: string, asOf: string): string {
  return `Since ${formatFinanceDate(since)} · as of ${formatFinanceDate(asOf)}`;
}

// ─────────────────────────────────────────────────────────
// R2 item 1 (docs/plans/2026-09-02-nightly-cogs-replan.md, D5): nightly
// batched COGS replan state + "Recompute now".
// ─────────────────────────────────────────────────────────

const COGS_REPLAN_QUERY_KEY = ['cogs-replan'] as const;

/** GET /api/reports/cogs/replan. Polls every 5s while a run is in flight
 * (runningSince set) and stops polling once it clears — the same shape as
 * DossierPage's discovery-run poll. */
export function useCogsReplanState(enabled = true) {
  return useQuery({
    queryKey: COGS_REPLAN_QUERY_KEY,
    enabled,
    queryFn: () =>
      apiFetch('/api/reports/cogs/replan', { responseSchema: CogsReplanStateResponse }),
    refetchInterval: (query) => (query.state.data?.state.runningSince != null ? 5000 : false),
  });
}

/** POST /api/reports/cogs/replan ("Recompute now"). On success, seeds the
 * cogs-replan query with the fresh state (so the button reflects it before
 * the next poll) and invalidates finance-summary (which embeds the same
 * state) so the P&L picks up the change too. */
export function useRequestCogsReplan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch('/api/reports/cogs/replan', {
        method: 'POST',
        responseSchema: CogsReplanStateResponse,
      }),
    onSuccess: (data) => {
      qc.setQueryData(COGS_REPLAN_QUERY_KEY, data);
      void qc.invalidateQueries({ queryKey: ['finance-summary'] });
    },
  });
}

export type CogsReplanBannerTone = 'idle' | 'pending' | 'running' | 'error';

export interface CogsReplanBannerCopy {
  tone: CogsReplanBannerTone;
  title: string;
  detail: string;
}

/** 'never' for a null timestamp; otherwise a short "N unit(s) ago" bucketed
 * to seconds/minutes/hours/days (a future or unparsable timestamp reads as
 * "just now" rather than a confusing negative duration). */
export function formatRelativeTime(iso: string | null, now: Date = new Date()): string {
  if (iso == null) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const diffSeconds = Math.max(0, Math.round((now.getTime() - then) / 1000));
  if (diffSeconds < 60) return 'just now';
  const diffMinutes = Math.round(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}

/** R2 item 1, task 3 (docs/plans/2026-09-03-incremental-replan.md, D3;
 *  fix pass finding 9): "Recomputing 3 of 12…" once at least one chunk has
 *  landed; "Recomputing… (5 steps)" once the run's total is known but
 *  before the first chunk lands ("0 of 5" reads like something already
 *  went wrong — a step count reads as still-starting); the bare
 *  "Recomputing…" spinner copy only when `run` itself is unknown (the
 *  legacy single-message shape, which never reports `run`, or the instant
 *  right after `beginCogsReplanRun` commits but before the run row is
 *  readable back). */
export function cogsReplanProgressLabel(run: CogsReplanState['run']): string {
  if (run == null) return 'Recomputing…';
  if (run.chunksDone === 0) {
    return `Recomputing… (${run.chunksTotal} step${run.chunksTotal === 1 ? '' : 's'})`;
  }
  return `Recomputing ${run.chunksDone} of ${run.chunksTotal}…`;
}

/** D5's four banner states, in priority order: a run in flight always wins
 * (its own copy), then a surfaced failure from the last run, then a pending
 * count awaiting nightly batch, then the quiet "up to date" line. */
export function cogsReplanBanner(
  state: CogsReplanState,
  now: Date = new Date()
): CogsReplanBannerCopy {
  const lastReconciled = formatRelativeTime(state.lastReconciledAt, now);
  if (state.runningSince != null) {
    return { tone: 'running', title: cogsReplanProgressLabel(state.run), detail: '' };
  }
  if (state.lastError != null) {
    return { tone: 'error', title: `Last recompute failed: ${state.lastError}`, detail: '' };
  }
  if (state.pendingSince != null) {
    const count = state.pendingCount;
    return {
      tone: 'pending',
      title: `Landed COGS as of ${lastReconciled}`,
      detail: `${count} change${count === 1 ? '' : 's'} since, recomputes nightly`,
    };
  }
  // A null lastReconciledAt with nothing pending/running means no recompute
  // has ever run for this tenant — "as of never" reads like a stale/broken
  // state rather than the true "nothing to report yet" state.
  if (state.lastReconciledAt == null) {
    return {
      tone: 'idle',
      title: 'Landed COGS is current',
      detail: 'No recompute has been recorded yet.',
    };
  }
  return { tone: 'idle', title: `Landed COGS as of ${lastReconciled}`, detail: '' };
}

export function formatFinanceCurrency(cents: number | null, currency: string): string {
  return formatFeeCenterCurrency(cents, currency);
}

export function formatFinanceDelta(cents: number | null, currency: string): string {
  return formatFeeCenterDelta(cents, currency);
}

/** "Aug 3, 2026" — en-US, UTC, matching formatFeeCenterDate's style, for
 * invoice date lines (R2 item 5, 2026-09-02 cold-viewer review). Keeps ISO
 * strings out of the visible UI. */
export function formatFinanceDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export interface FinanceCoverageCopy {
  title: string;
  detail: string;
}

export function financeCoverageCopy(
  value: FinanceSummaryCoverage,
  source: 'settlement' | 'sales'
): FinanceCoverageCopy | null {
  if (value.complete) return null;
  const noun = source === 'settlement' ? 'Settlement' : 'Sales';
  if (value.status === 'backfilling') {
    return {
      title: `${noun} data is still backfilling`,
      detail:
        source === 'settlement'
          ? 'Amounts remain unavailable until the selected settlement window is complete.'
          : 'Sales totals remain unavailable until the selected report window is complete.',
    };
  }
  if (value.status === 'pending') {
    return {
      title: `${noun} data is pending`,
      detail:
        source === 'settlement'
          ? 'Amounts remain unavailable until the selected settlement report is ready.'
          : 'Sales totals remain unavailable until the selected report is ready.',
    };
  }
  return {
    title: `${noun} coverage is unavailable`,
    detail:
      source === 'settlement'
        ? 'Missing settlement evidence cannot be treated as zero activity or charges.'
        : 'A missing report cannot be treated as zero sales.',
  };
}

export type FinanceAccountingCoverageStatus =
  | FinanceSummaryTask10Coverage['status']
  | 'legacy_absent';

export interface FinanceAccountingCoverageCopy {
  status: FinanceAccountingCoverageStatus;
  label: string;
  title: string;
  detail: string;
  allowsOfficialPeriodTotal: boolean;
  evidenceMayBeAvailable: boolean;
}

export type FinanceAccountingCoverageSource = 'cogs' | 'warehouseExpenses' | 'operatingExpenses';

/** Describe Task 10 coverage without treating missing accounting facts as zero. */
export function financeAccountingCoverageCopy(
  value: FinanceSummaryTask10Coverage | undefined,
  source: FinanceAccountingCoverageSource
): FinanceAccountingCoverageCopy {
  const noun =
    source === 'cogs'
      ? 'COGS'
      : source === 'warehouseExpenses'
        ? 'Warehouse expenses'
        : 'Operating expenses';
  const lowerNoun =
    source === 'cogs'
      ? 'COGS'
      : source === 'warehouseExpenses'
        ? 'warehouse expense'
        : 'operating expense';
  // R1 item 6: overhead (VA pay, software, marketing, card fees) is entered
  // on the Expenses page, not derived from any Amazon or warehouse source —
  // point there whenever this coverage is incomplete.
  const pageHint =
    source === 'operatingExpenses' ? ' Overhead is entered on the Expenses page.' : '';
  const rawStatus = value?.status;
  const status: FinanceAccountingCoverageStatus =
    rawStatus === 'complete' && value?.complete !== true
      ? 'unknown'
      : (rawStatus ?? 'legacy_absent');

  if (status === 'complete') {
    return {
      status,
      label: 'Complete',
      title: `${noun} coverage is complete`,
      detail: `Official ${noun} totals may be shown for the selected period.${pageHint}`,
      allowsOfficialPeriodTotal: true,
      evidenceMayBeAvailable: true,
    };
  }
  if (status === 'partial') {
    return {
      status,
      label: 'Partial',
      title: `${noun} coverage is partial`,
      detail: `${noun} evidence may be available, but it cannot establish an official period total.${pageHint}`,
      allowsOfficialPeriodTotal: false,
      evidenceMayBeAvailable: true,
    };
  }
  if (status === 'backfilling') {
    return {
      status,
      label: 'Backfilling',
      title: `${noun} data is still backfilling`,
      detail: `Official ${lowerNoun} totals remain unavailable until coverage is complete.${pageHint}`,
      allowsOfficialPeriodTotal: false,
      evidenceMayBeAvailable: true,
    };
  }
  if (status === 'pending') {
    return {
      status,
      label: 'Pending',
      title: `${noun} data is pending`,
      detail: `Official ${noun} totals remain unavailable until the selected report is ready.${pageHint}`,
      allowsOfficialPeriodTotal: false,
      evidenceMayBeAvailable: true,
    };
  }
  if (status === 'legacy_absent') {
    return {
      status,
      label: 'Unavailable',
      title: `${noun} coverage is unavailable`,
      detail: `Legacy data has no ${lowerNoun} coverage; missing data cannot be treated as zero.${pageHint}`,
      allowsOfficialPeriodTotal: false,
      evidenceMayBeAvailable: false,
    };
  }
  return {
    status: 'unknown',
    label: 'Unknown',
    title: `${noun} coverage is unavailable`,
    detail: `Missing ${lowerNoun} evidence cannot be treated as zero activity or cost.${pageHint}`,
    allowsOfficialPeriodTotal: false,
    evidenceMayBeAvailable: false,
  };
}

/** Name which accounting facts are blocking net income, for the Net income
 * detail copy — calls out the operating expense component by name instead
 * of a generic "an accounting fact" when it's the one missing (R1 item 6). */
export function financeMissingIncomeComponentsLabel(available: {
  cogs: boolean;
  warehouseExpense: boolean;
  operatingExpense: boolean;
}): string {
  const missing: string[] = [];
  if (!available.cogs) missing.push('landed COGS');
  if (!available.warehouseExpense) missing.push('warehouse expense');
  if (!available.operatingExpense) missing.push('operating expense');
  if (missing.length === 0) return 'an accounting fact';
  if (missing.length === 1) return missing[0]!;
  if (missing.length === 2) return `${missing[0]} and ${missing[1]}`;
  return `${missing.slice(0, -1).join(', ')}, and ${missing[missing.length - 1]}`;
}

const FINANCE_INCOME_REASONS: Record<Exclude<FinanceSummaryIncomeStatus, 'complete'>, string> = {
  partial: 'Net income is partial because accounting coverage is incomplete.',
  blocked_missing_revenue: 'Net income is unavailable because revenue is missing.',
  blocked_missing_sales: 'Net income is unavailable because sales data is missing.',
  blocked_missing_landed_cogs: 'Net income is unavailable because landed COGS is missing.',
  blocked_missing_warehouse_expense:
    'Net income is unavailable because warehouse expense coverage is missing.',
  blocked_missing_operating_expense: 'Operating expenses are not attested for this month.',
};

/** Return the server blocker when present, with a specific safe legacy fallback. */
export function financeIncomeReason(
  status: FinanceSummaryIncomeStatus | undefined,
  serverReason: string | null | undefined
): string | null {
  const reason = typeof serverReason === 'string' ? serverReason.trim() : '';
  if (reason) return reason;
  if (status === 'complete') return null;
  return status
    ? FINANCE_INCOME_REASONS[status]
    : 'Net income is unavailable because accounting coverage is unavailable.';
}

export function financeDateBasisLabel(basis: string | null | undefined): string {
  if (basis === 'purchase_date_utc') return 'Sale purchase date — COGS month';
  if (basis === 'recognized_at_utc') return 'Consumption ledger recorded at';
  return 'Date basis unavailable — legacy data';
}

export function financeSourceEventLabel(event: string | null | undefined): string {
  if (event === 'consume') return 'Consumption';
  if (event === 'reverse') return 'Reversal';
  return 'Unknown activity';
}

export function formatFinanceSourceUnits(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const formatted = value.toLocaleString('en-US');
  if (value > 0) return `+${formatted} units`;
  if (value < 0) return `${formatted.replace(/^-/, '−')} units`;
  return '0 units';
}

export function formatFinanceSourceCost(
  value: number | null | undefined,
  currency: string
): string {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value > 0) return `+${formatFinanceCurrency(value, currency)}`;
  if (value < 0) {
    const formatted = formatFinanceCurrency(value, currency);
    return formatted.replace(/^-/, '−');
  }
  return formatFinanceCurrency(0, currency);
}

export function financeSourceCostLabel(costLayerId: string | null | undefined): string {
  return costLayerId == null ? 'Base vendor cost only' : 'Warehouse layer cost';
}

type FinanceWarehouseLinkSource = Partial<
  Pick<
    FinanceSummarySource,
    'costLayerId' | 'warehouseAllocationId' | 'chargeId' | 'invoiceId' | 'invoiceLineId'
  >
>;

/** Explain a broken warehouse ancestry chain; base vendor rows have no link to report. */
export function financeWarehouseLinkCopy(
  source: FinanceWarehouseLinkSource | null | undefined
): string | null {
  if (!source?.costLayerId) return null;
  return source.warehouseAllocationId && source.chargeId && source.invoiceId && source.invoiceLineId
    ? null
    : 'Warehouse link unavailable';
}

export function financeIncomeBlockedCopy(): string {
  return 'Warehouse allocations and sale-to-lot cost linkage are not yet available.';
}

const FINANCE_CATEGORY_LABELS: Record<FeeCenterActivityCategory, string> = {
  selling_fees: 'Selling fees',
  fba_fulfillment: 'FBA fulfillment',
  inbound_shipping: 'Inbound and shipping',
  storage_fees: 'Storage fees',
  removals: 'Removals',
  subscription: 'Subscription',
  reimbursements: 'Reimbursements',
  tax_withheld: 'Tax withheld',
  promotions_adjustments: 'Promotions and adjustments',
  other_amazon_activity: 'Other Amazon activity',
};

export function financeCategoryLabel(category: FeeCenterActivityCategory): string {
  return FINANCE_CATEGORY_LABELS[category];
}

export function financeCategoryDirection(cents: number): 'Charge' | 'Credit' {
  return feeCenterActivityDirection(cents);
}

export function financePayoutStatusLabel(
  status: FinanceSummaryPayout['reconciliationStatus']
): string {
  if (status === 'reconciled') return 'Reconciled';
  if (status === 'partial') return 'Partial';
  return 'Unverified';
}

export function financePayoutReconciliationCopy(
  status: FinanceSummaryPayout['reconciliationStatus'],
  deltaCents: number | null,
  currency: string
): string {
  if (status === 'reconciled') return 'Matches the stored Amazon settlement lines.';
  if (status === 'partial') {
    return `Header-to-line gap: ${formatFinanceDelta(deltaCents, currency)} remains unexplained.`;
  }
  return 'Could not be reconciled because the settlement header or stored lines are unavailable.';
}

export function payoutDateCopy(value: string | null): string {
  return value
    ? `Deposit date: ${formatFeeCenterDate(value)}`
    : 'Undated settlement — excluded from period payout totals';
}

export function financeSettlementHeaderCopy(): string {
  return 'Amazon report header — not a bank-confirmed deposit.';
}

// ─────────────────────────────────────────────────────────
// Payouts section (2026-09-02 redesign, Faraz: "make payout collapsable ...
// I am not sure I understand the whole area"). The section collapses by
// default; financePayoutStatusLabel/financePayoutReconciliationCopy above
// stay in place for the per-card "Show detail" disclosure.
// ─────────────────────────────────────────────────────────

const PAYOUTS_OPEN_STORAGE_KEY = 'finance.payouts.open';

/** Collapsed by default. Every read is wrapped: private browsing, a full
 * quota, or no `window` at all (this module's tests render under vitest's
 * `environment: 'node'`, where `window` frequently doesn't exist) all fall
 * back to "closed". */
export function loadPayoutsOpen(): boolean {
  try {
    return window.localStorage.getItem(PAYOUTS_OPEN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function savePayoutsOpen(open: boolean): void {
  try {
    window.localStorage.setItem(PAYOUTS_OPEN_STORAGE_KEY, open ? 'true' : 'false');
  } catch {
    // Storage unavailable — open/closed just won't persist across reloads.
  }
}

export interface PayoutCurrencySummary {
  currency: string;
  count: number;
  totalCents: number;
}

/** Per-currency settlement count and total, in first-seen currency order.
 * A null settlementTotalCents contributes 0 to the sum but the settlement
 * still counts toward that currency's count. */
export function payoutSectionSummary(
  payouts: readonly FinanceSummaryPayout[]
): PayoutCurrencySummary[] {
  const order: string[] = [];
  const byCurrency = new Map<string, PayoutCurrencySummary>();
  for (const payout of payouts) {
    let entry = byCurrency.get(payout.currency);
    if (!entry) {
      entry = { currency: payout.currency, count: 0, totalCents: 0 };
      byCurrency.set(payout.currency, entry);
      order.push(payout.currency);
    }
    entry.count += 1;
    entry.totalCents += payout.settlementTotalCents ?? 0;
  }
  return order.map((currency) => byCurrency.get(currency)!);
}

/** Plain-language reconciliation status for a payout card's badge — the
 * seller-facing counterpart to financePayoutStatusLabel, which stays
 * "Reconciled/Partial/Unverified" for the detail disclosure. */
export function payoutStatusPlainLabel(
  status: FinanceSummaryPayout['reconciliationStatus'],
  deltaCents: number | null,
  currency: string
): string {
  if (status === 'reconciled') return 'Line items match';
  if (status === 'partial') {
    return `Line items off by ${formatFinanceDelta(deltaCents, currency)}`;
  }
  return 'Line items not loaded';
}

// ─────────────────────────────────────────────────────────
// Warehouse expense period attestation (R1 item 4, 2026-09-01 design doc)
// ─────────────────────────────────────────────────────────

export interface ExpenseAttestationMonthStatus {
  month: string;
  /** True when at least one currency's warehousePeriodExpenseCents is
   * withheld (null) for this month — unattested or stale. */
  blocked: boolean;
}

/** Every month present in the response, each flagged blocked when any
 * currency withholds its warehouse expense OR its operating expense (D3:
 * one attestation covers both ledgers — the same completeMonths gate feeds
 * both kernel inputs, so either one being withheld blocks the month).
 * operatingExpenseCents is optional in the schema (legacy responses omit
 * it entirely); only a REPORTED null counts as blocked — an absent field
 * is not treated as withheld. Attestation is one per-month declaration
 * across all currencies (D1) — a single combined list, not one per
 * currency. */
export function expenseAttestationMonthStatuses(
  activity: readonly FinanceSummarySettlementActivity[]
): ExpenseAttestationMonthStatus[] {
  const byMonth = new Map<string, boolean>();
  for (const currencyActivity of activity) {
    for (const month of currencyActivity.monthly) {
      const operatingReported = Object.prototype.hasOwnProperty.call(
        month,
        'operatingExpenseCents'
      );
      const blocked =
        month.warehousePeriodExpenseCents == null ||
        (operatingReported && month.operatingExpenseCents == null);
      byMonth.set(month.month, (byMonth.get(month.month) ?? false) || blocked);
    }
  }
  return [...byMonth.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, blocked]) => ({ month, blocked }));
}

/** 'YYYY-MM' -> 'June 2026'. */
export function expenseAttestationMonthLabel(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return new Date(Date.UTC(year, monthNumber - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** D3: one confirm button per month covers both ledgers — confirms every
 * warehouse AND overhead expense for the month is entered. Plain-language
 * rename of "attest" (Faraz screenshot review, 2026-09-02) — the title uses
 * the full month label, the body the bare month name. */
export function expenseAttestationDialogCopy(
  month: string,
  stale: boolean
): { title: string; body: string } {
  const label = expenseAttestationMonthLabel(month);
  const monthName = financeMonthName(month);
  return stale
    ? {
        title: `Re-confirm ${label} expenses?`,
        body: `Expenses for ${monthName} changed since you confirmed them — you're confirming again that every expense for ${monthName}, warehouse and overhead, is entered. Net income re-unlocks for this month.`,
      }
    : {
        title: `Confirm ${label} expenses?`,
        body: `You're confirming that every expense for ${monthName}, warehouse and overhead, is entered. Net income unlocks for this month.`,
      };
}
